import { Box, Checkbox, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel } from "@mui/material";
import { translate } from "../../translate/translate";
import * as React from "react";
import { useEffect } from "react";
import TableMenuComponent from "../table-menu/table-menu.component";
import { useAuth } from "../../contexts/auth-context";
import { Value } from "./value-row-column.component";
import Divider from "@mui/material/Divider";
import { Choice } from "../../dto/choice";
import { ErrorComponent } from "../error/error.component";
import { CheckCircle, ChevronRight, ExpandMore, RemoveCircle } from "@mui/icons-material";
import dayjs from "dayjs";

export type Order = "asc" | "desc";

const TablePaginationComponent = (props: any) => {
  const {
    displayedColumnsShow,
    displayedColumns,
    displayedTranslate,
    handleSwitch,
    columnStyles,
    field,
    fieldList,
    readOnlyBooleanFieldList,
    service,
    dateColumns,
    dateWithoutTime,
    showActions,
    handleDuplicate,
    handleRowSelect,
    selectedRows,
    renderCellHandler,
    headerCellCustomStyleHandler,
    cellCustomStyleHandler,
    rowCustomStyleHandler,
    stickyHeader,
    disablePointer,
    handleSingleClick,
    openRows,
    collapse = false,
    CollapseComponent,
    renderRowExtra,
    sortingProps,
    removeStripe = false,
    canDelete,
    headerLabels = {},
    legendComponent,
    tableLayout = "fixed",
    tableMinWidth,
  } = props;

  const {
    sortingColumns,
    onRequestSort,
    order,
    orderBy,
  } = sortingProps || {};

  const { user } = useAuth();

  const [choices, setChoices] = React.useState<Choice[]>([]);
  const [choicesList, setChoicesList] = React.useState<any>({});

  const getChoices = () => {
    if (field) {
      service.getChoices(field).then((response: any) => {
        setChoices(response?.map((item: any) => item));
      }).catch((error: any) => {
        ErrorComponent(error);
      });
    }
  };

  const getChoicesList = () => {
    if (fieldList) {
      fieldList.forEach((field: string) => {
        service.getChoices(field).then((response: any) => {
          setChoicesList((value: any) => ({
            ...value,
            [field]: response?.map((item: any) => item),
          }));
        }).catch((error: any) => {
          ErrorComponent(error);
        });
      });
    }
  };

  useEffect(() => {
    getChoices();
    getChoicesList();
  }, []);

  const translateTitleColumn = (column: string, index: number): string => {
    if (headerLabels[column]) {
      return headerLabels[column];
    }

    return displayedColumnsShow === undefined || displayedColumnsShow.length !== displayedColumns.length
      ? translate.t(column)
      : translate.t(displayedColumnsShow[index]);
  };

  const translateRowColumn = (column: string, index: number, row: any): string => {
    const value = String(Value(row, column));
    return (
      (value === "" || value === undefined || value === null)
        ? "-"
        : (
          displayedTranslate !== undefined && displayedTranslate.length > 0 &&
          displayedTranslate.find((choice: any) => choice == column) !== undefined
        )
          ? translate.t(value)
          : value
    );
  };

  const changeSwitch = (row: any): boolean => {
    return "email" in row && "username" in row && row.id === user.id;
  };

  const dateFormat = (column: string, row: any) => {
    if (!String(Value(row, column))) return "-";

    const cleanedDateTime = String(Value(row, column)).replace(/\.\d+/, "");

    const timeFormat = translate.language === "pt" ? "HH:mm" : "hh:mm A";
    const formattedDate = dayjs(cleanedDateTime).format("L");
    const formattedTime = dayjs(cleanedDateTime).format(timeFormat);

    if (Array.isArray(dateWithoutTime) && dateWithoutTime.includes(column)) {
      return `${formattedDate}`;
    }

    return `${formattedDate} ${formattedTime}`;
  };

  const switchColumns = new Set([
    "is_superuser",
    "is_active",
    "active",
    "default",
    "is_top_bottom",
  ]);

  const isSwitch = (column: string): boolean => switchColumns.has(column);

  const isDate = (column: string): boolean => {
    return dateColumns === undefined
      ? false
      : dateColumns.includes(column);
  };

  const renderCell = (column: string, index: number, row: any) => {
    if (column === field) {
      const choice = choices.find((choice: any) => choice.value === row[column]);
      return choice?.display_name;
    }
    if (fieldList && fieldList.includes(column)) {
      const choice = choicesList[column].find((choice: any) => choice.value === row[column]);
      return choice?.display_name;
    }
    if (readOnlyBooleanFieldList && readOnlyBooleanFieldList.includes(column)) {
      return row[column] ? (
        <CheckCircle sx={{ color: "#00B894" }} />
      ) : (
        <RemoveCircle sx={{ color: "#90A4AE" }} />
      );
    }
    return column === "isRowSelectable" ? (
      <Checkbox
        checked={selectedRows.some((selectedRow: any) => selectedRow.id === row.id)}
        onChange={() => handleRowSelect(row)}
      />
    ) : isSwitch(column) ? (
      <Switch
        color="primary"
        checked={Value(row, column)}
        onChange={() =>
          !changeSwitch(row) && handleSwitch(row, column)
        }
        disabled={row.id === user.id && row.username === user.username}
      />
    ) : (
      isDate(column)
        ? dateFormat(column, row)
        : (
          column === "color" ?
            <Box
              width="20px"
              height="20px"
              borderRadius="50%"
              bgcolor={row[column]}
            ></Box>
            : translateRowColumn(column, index, row)
        )
    );
  };

  const showPaginated = (from: number, to: number, count: number) => {
    return `${from} - ${to} ${translate.t("of")} ${count}`;
  };

  const shouldRenderCollapse = (row: any): boolean => {
    return collapse && CollapseComponent && row?.children_components?.length > 0;
  };

  const getColumnStyle = (column: string, index: number) => {
    if (!columnStyles) return {};

    const legacyColumnWidth = columnStyles[index + 1];
    if (legacyColumnWidth) {
      return typeof legacyColumnWidth === "object"
        ? legacyColumnWidth
        : { width: legacyColumnWidth };
    }

    const namedColumnStyle = columnStyles[column];
    if (!namedColumnStyle) return {};

    return typeof namedColumnStyle === "object"
      ? namedColumnStyle
      : { width: namedColumnStyle };
  };

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Paper sx={{ width: "100%", minWidth: 0 }}>
        <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
          <Table stickyHeader={stickyHeader} size="small" sx={{ tableLayout, width: "100%", minWidth: tableMinWidth, border: "1px solid #cfd8dc" }}>
            <TableHead>
              <TableRow key="header">
                {displayedColumnsShow
                  .filter((col: string) => col !== "action")
                  .map((column: string, index: any) => {
                    const displayedColumn = displayedColumns[index];
                    const baseStyle = {
                      lineHeight: "1.2",
                      padding: "14px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "white",
                      backgroundColor: "#3949AB",
                      width:
                        columnStyles && (columnStyles[column] || columnStyles[index + 1])
                          ? columnStyles[column] || columnStyles[index + 1]
                          : undefined,
                      ...getColumnStyle(displayedColumn, index),
                    };

                    const customStyle = headerCellCustomStyleHandler
                      ? headerCellCustomStyleHandler(column)
                      : {};

                    if (column === "select" && props.renderHeaderCheckbox) {
                      return (
                        <TableCell
                          key={`header-${column}-${index}`}
                          sx={{
                            ...baseStyle,
                            ...customStyle,
                            textAlign: "center",
                          }}
                        >
                          {props.renderHeaderCheckbox()}
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell
                        key={`header-${column}-${index}`}
                        sx={{ ...baseStyle, ...customStyle }}
                        sortDirection={orderBy === column ? order : false}
                      >
                        {sortingColumns && sortingColumns.includes(column) ? (
                          <TableSortLabel
                            active={orderBy === column && order !== ""}
                            direction={orderBy === column ? order : "asc"}
                            onClick={() => onRequestSort(column)}
                            sx={{
                              fontWeight: 700,
                              fontSize: "16px",

                              "&:hover": { color: "inherit" },
                              "&.Mui-active": { color: "inherit" },

                              "& .MuiTableSortLabel-icon": {
                                color: "white !important",
                              },
                            }}
                          >
                            {translateTitleColumn(column, index)}
                          </TableSortLabel>
                        ) : (
                          <Box
                            sx={{
                              fontWeight: 700,
                              fontSize: "16px",
                            }}
                          >
                            {translateTitleColumn(column, index)}
                          </Box>
                        )}
                      </TableCell>

                    );
                  })}
                {displayedColumns.includes("action") && (
                  <TableCell
                    key="header-action"
                    align="center"
                    sx={{
                      lineHeight: "1.2",
                      padding: "14px",
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "white !important",
                      backgroundColor: "#3949AB",
                      width: "120px",
                    }}>
                    {props.actionHeaderLabel ?? translate.t("actions")}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody sx={{ cursor: disablePointer ? "default" : "pointer" }}>
              {props.state && props.state.length > 0 ? (
                props.state.map((row: any, index: any) => (
                  <React.Fragment key={`fragment-${row.id}-${index}`}>
                    <TableRow
                      key={`row-${row.id}-${index}`}
                      className={removeStripe ? "no-stripe" : ""}
                      sx={{
                        "&:last-child td, &:last-child th": {
                          borderBottom: "1px solid #e0e0e0",
                        },
                        ...rowCustomStyleHandler ? rowCustomStyleHandler(row) : {},
                      }}
                      onDoubleClick={() => {
                        if (props.handleDoubleClick) {
                          props.handleDoubleClick(row);
                        }
                      }}
                      onClick={() => {
                        if (collapse) {
                          handleSingleClick(row.id);
                        }
                        if (props.handleRowClick) {
                          props.handleRowClick(row);
                        }
                      }}
                    >
                      {displayedColumns
                        .filter((obj: string) => obj !== "action")
                        .map((column: string, index: any) => (
                          <TableCell
                            key={`cell-${row.id}-${index}`}
                            sx={{
                              ...getColumnStyle(column, index),
                              wordBreak: "break-word",
                              fontSize: "14px",
                              lineHeight: "20px",
                              justifyContent: "center",
                              ...cellCustomStyleHandler ? cellCustomStyleHandler(column, row) : {},
                            }}
                          >
                            {index === 0 && collapse && row?.children_components ? (
                              row?.children_components && row?.children_components?.length > 0 ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    float: "left",
                                    width: "20px",
                                    height: "20px",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    cursor: "pointer",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    props.handleSingleClick(row.id);
                                  }}
                                >
                                  {props.openRows[row.id] ? (
                                    <ExpandMore sx={{ color: "#546E7A" }} />
                                  ) : (
                                    <ChevronRight sx={{ color: "#546E7A" }} />
                                  )}
                                </Box>
                              ) : null
                            ) : null}

                            {renderCellHandler && renderCellHandler[column] ? renderCellHandler[column](column, index, row, { choices }) : renderCell(column, index, row)}
                          </TableCell>
                        ))}
                      {displayedColumns.includes("action") && ((
                        renderCellHandler?.["action"] ? (
                          <TableCell
                            key={`cell-action-${row.id}`}
                            align="center"
                          >
                            {renderCellHandler["action"]("action", 0, row)}
                          </TableCell>
                        ) : <TableMenuComponent
                          row={row}
                          handleDelete={props.handleDelete}
                          handleOpenDialogHistory={props.handleOpenDialogHistory}
                          editWithDialog={props.editWithDialog}
                          showActions={showActions}
                          handleDuplicate={handleDuplicate}
                          canDelete={canDelete ? canDelete(row) : null}
                        />
                      ))}
                    </TableRow>

                    {shouldRenderCollapse(row) && (
                      <CollapseComponent
                        open={openRows[row.id]}
                        row={row}
                        openRows={openRows}
                        handleRowToggle={handleSingleClick}
                      />
                    )}

                    {renderRowExtra && renderRowExtra(row, displayedColumns.length)}
                  </React.Fragment>
                ))
              ) : (
                <TableRow key="empty-row">
                  <TableCell
                    key="empty-cell"
                    colSpan={displayedColumns.length}
                    sx={{
                      textAlign: "center",
                      fontSize: "14px",
                      padding: "8px",
                    }}
                  >
                    {translate.t("empty_listing")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ minWidth: 0, overflow: "visible", flexWrap: "nowrap" }}
        >
          <Box px={2} sx={{ flex: 1, minWidth: 0, width: "auto" }}>
            {legendComponent}
          </Box>
          <TablePagination
            sx={{
              color: "#45485F",
              flex: "0 1 auto",
              minWidth: 0,
              minHeight: "52px",
              overflow: "visible",
              textAlign: "end",
              width: "fit-content",
              "& .MuiTablePagination-toolbar": {
                minHeight: "52px",
                paddingLeft: 0,
                paddingRight: "4px",
                flexWrap: "nowrap",
              },
              "& .MuiTablePagination-spacer": {
                display: "none",
              },
              "& .MuiTablePagination-selectLabel": {
                margin: 0,
              },
              "& .MuiTablePagination-input": {
                marginLeft: "4px",
                marginRight: "8px",
              },
              "& .MuiTablePagination-displayedRows": {
                margin: 0,
              },
              "& .MuiTablePagination-actions": {
                marginLeft: "6px",
                display: "flex",
                gap: 0,
              },
              "& .MuiTablePagination-actions .MuiIconButton-root": {
                padding: "4px",
              },
            }}
            component="div"
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            colSpan={12}
            count={Number(props.count ?? 0)}
            rowsPerPage={props.rowsPerPage}
            page={props.page}
            onPageChange={props.onPageChange}
            onRowsPerPageChange={props.handleChangeRowsPerPage}
            ActionsComponent={props.actionsComponent}
            labelRowsPerPage={translate.t("rows_per_page")}
            labelDisplayedRows={({ from, to, count }) =>
              showPaginated(from, to, count)
            }
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default TablePaginationComponent;
